<template>
  <Tippy class="UIDropdown" v-bind="props" :theme="dropdownTheme" :tag="'div'">
    <template #default="{ state }">
      <slot name="toggle" :state="state" :isVisible="state.isVisible">
        <UIDropdownToggle :isOpen="state.isVisible">
          <slot />
        </UIDropdownToggle>
      </slot>
    </template>

    <template #content="{ hide }">
      <slot name="content" :hide="hide">
        <div :class="['UIDropdown__menu', contentClass]" :style="styles">
          <slot name="before-content" :hide="hide" />
          <ul class="UIDropdown__menu-list">
            <slot
              v-for="(option, index) in items"
              :key="index"
              name="option-container"
              :option="option"
              :hide="hide"
            >
              <UIDropdownListItem
                @click.prevent="() => handleClickOption(option, hide)"
              >
                <slot name="option" :option="option" :hide="hide">
                  <button>
                    {{ option.label }}
                  </button>
                </slot>
              </UIDropdownListItem>
            </slot>
          </ul>
          <slot name="after-content" :hide="hide" />
        </div>
      </slot>
    </template>
  </Tippy>
</template>

<script lang="ts" setup generic="T">
import { Tippy } from 'vue-tippy'
import { Placement } from '@popperjs/core'
import UIDropdownToggle from './UIDropdownToggle.vue'
import { IIcons } from '../icons/types'
import { RouteLocationRaw, useRouter } from 'vue-router'
import UIDropdownListItem from './UIDropdownListItem.vue'
import { computed } from 'vue'

export type IUIDropdownOption = {
  label: string
  value: unknown
  to?: RouteLocationRaw
  href?: string
  icon?: IIcons
  isHidden?: boolean
  onClick?: (item?: unknown) => void
}

type ITippyProps = InstanceType<typeof Tippy>['$props']
interface Props extends /* @vue-ignore */ ITippyProps {
  label?: string
  interactive?: boolean
  appendTo?: Element | 'parent' | ((ref: Element) => Element)
  offset?: [number, number]
  items: T[]
  istooltip?: boolean
  trigger?: string
  placement?: Placement
  isDisabled?: boolean
  duration?: [number, number]
  theme?: string
  arrow?: boolean
  fontSize?: string
  contentClass?: string
  contentStyle?: object
  width?: string
  dropdownTheme?: 'topbar' | 'dropdown'
}

const emit = defineEmits<{
  (eventName: 'onSelect', value: T): void
}>()
const props = withDefaults(defineProps<Props>(), {
  appendTo: () => () => document.body,
  interactive: true,
  offset: () => [0, 1],
  trigger: 'click',
  placement: 'bottom-start',
  duration: () => [0, 0],
  theme: 'dropdown',
  arrow: false,
  fontSize: '16px',
  contentClass: '',
  contentStyle: () => ({}),
  dropdownTheme: 'dropdown',
})

const router = useRouter()
const handleClickOption = (option: T, hide: any) => {
  if (option.onClick) {
    option.onClick()
  }
  if (option.to) {
    router.push(option.to)
  }
  emit('onSelect', option)
  hide()
}

const styles = computed(() => ({
  '--dropdown-width': `var(--dropdown-width, ${props.width})`,
  'font-size': props.fontSize,
  ...props.contentStyle,
}))
</script>

<style scoped lang="scss">
.UIDropdown {
  display: inline-block;
  --toggle-size: 1rem;
}

// .UIDropdown__menu {
//   width: var(--dropdown-width);
//   border-radius: 4px;
//   padding: 0.5rem;
//   overflow: hidden;
//   box-shadow: 0 0 10px -2px rgba(9, 9, 45, 0.15);
//   background: $color-white;

//   .UIDropdown__menu-list {
//     @include customScrollbar;
//     font-size: inherit;
//     max-height: 25rem;
//     li {
//       list-style: none;
//     }
//   }
// }
</style>

<!-- Override tippy dropdown theme -->
<style lang="scss">
.tippy-box[data-theme~='dropdown'] {
  box-shadow: 0px 4px 8px -4px #0000001a;
  box-shadow: 0px 8px 16px -3px #0000001a;
  font-size: 0.875rem;
  line-height: 1.65rem;
  border-radius: 12px;
  overflow: hidden;
  background: transparent;
  padding: 0;
  color: $color-ui-gray-cool-700;
  width: 100%;
  max-width: unset !important;

  .tippy-content {
    @include font-size-12px;
    padding: 0;
    a {
      color: $color-ui-gray-cool-700;
      text-decoration: none;
    }
  }
}
.tippy-box[data-theme~='topbar'] {
  border: 1px solid $color-ui-border-secondary;
  border-radius: 16px;
  box-shadow: 0px 2px 2px -1px #0010350a;
  box-shadow: 0px 4px 6px -2px #00103508;
  box-shadow: 0px 12px 16px -4px #00103514;
  background-color: $color-white;

  .tippy-content {
    @include font-size-14px;
    overflow: hidden;
    padding: 0;
  }
}
</style>
